// Utility helpers for Dubai-time date/time formatting + day grouping.
// Shared by Chat.jsx and Feed.jsx to keep the hygiene round DRY.
import React from "react";

const TZ = "Asia/Dubai";

const dateTimeFmt = new Intl.DateTimeFormat("en-GB", {
  timeZone: TZ, day: "2-digit", month: "short", year: "numeric",
  hour: "2-digit", minute: "2-digit", hour12: false,
});
const dayKeyFmt = new Intl.DateTimeFormat("en-CA", {
  timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit",
}); // => "YYYY-MM-DD" in Dubai time
const dayLabelFmt = new Intl.DateTimeFormat("en-GB", {
  timeZone: TZ, day: "2-digit", month: "short", year: "numeric",
});

/** Format "12 Oct 2026 · 03:11" in Dubai time. */
export function fmtDateTime(iso) {
  if (!iso) return "—";
  const parts = dateTimeFmt.formatToParts(new Date(iso));
  const g = (t) => parts.find((p) => p.type === t)?.value || "";
  return `${g("day")} ${g("month")} ${g("year")} · ${g("hour")}:${g("minute")}`;
}

/** Dubai-local YYYY-MM-DD key for a timestamp. */
export function dubaiDayKey(iso) {
  if (!iso) return "";
  return dayKeyFmt.format(new Date(iso));
}

/** Human label for a day key: "Today" / "Yesterday" / "10 Oct 2026". */
export function dayLabel(dayKey) {
  const todayKey = dayKeyFmt.format(new Date());
  const y = new Date(); y.setDate(y.getDate() - 1);
  const yKey = dayKeyFmt.format(y);
  if (dayKey === todayKey) return "Today";
  if (dayKey === yKey) return "Yesterday";
  const [year, month, day] = dayKey.split("-");
  // Rebuild a date at local noon to avoid TZ drift.
  const d = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 12));
  return dayLabelFmt.format(d);
}

/** Render a day separator row (minimal, mono, muted). */
export function DaySeparator({ dayKey }) {
  return (
    <div
      className="flex items-center gap-3 py-2 select-none"
      data-testid={`day-sep-${dayKey}`}
    >
      <div className="h-px flex-1 bg-[color:var(--line)]" />
      <span className="font-mono text-[10.5px] uppercase tracking-[0.15em] text-[color:var(--muted)]">
        {dayLabel(dayKey)}
      </span>
      <div className="h-px flex-1 bg-[color:var(--line)]" />
    </div>
  );
}
